export const aiEssentialsSessionItems = [
	{
		id: 40101,
		type: "MCSS",
		content: {
			stem: 'A marketing associate is using an AI chatbot to draft a product description. The associate types: "Write something about our new product."\n\nThe AI returns a vague, generic paragraph that does not mention the product\'s key features or target audience.\n\nWhich action should the associate take to get a more useful response?',
			choices: [
				{
					id: "e1f2a3b4-1001-4a01-b001-112233440001",
					stem: "Submit the same prompt again and hope for a different result",
					text: "Submit the same prompt again and hope for a different result",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "e1f2a3b4-1001-4a01-b001-112233440002",
					stem: "Rewrite the prompt to include the product name, key features, target audience, and desired tone",
					text: "Rewrite the prompt to include the product name, key features, target audience, and desired tone",
					isCorrect: true,
					explanation:
						'**Rewriting the prompt with specific details** — such as the product name, key features, target audience, and desired tone — is the most effective way to improve the quality of an AI assistant\'s response. This approach applies the core principle of **effective prompt engineering**: the more relevant context you provide, the more targeted and useful the output will be.\n\nA well-constructed prompt communicates **purpose** (what you want), **context** (background information), **persona** (who the audience is or what voice to use), and **constraints** (length, tone, format). When any of these elements are missing, the AI lacks the information needed to generate a relevant response and will default to generic output.\n\nThe other options are **not effective strategies**:\n\n- **"Submit the same prompt again and hope for a different result"** may produce slightly different wording due to the randomness in text generation, but the **lack of specificity in the prompt means the output will remain generic**. Without additional context, the AI has no way to know what product, audience, or tone to focus on.\n\n- **"Switch to a completely different AI tool, since this one clearly cannot handle the task"** misidentifies the problem. The issue is not the tool — it is the **quality of the prompt**. Most modern AI assistants are capable of producing strong product descriptions when given clear, detailed instructions. Changing tools without improving the prompt will yield similar results.\n\n- **"Ask the AI to make the response longer so it covers more ground"** increases the word count but does **not improve relevance**. A longer generic response is still a generic response. Length without direction leads to filler content, not useful output.\n\n**Key Takeaways:**\n- **Specific, detailed prompts produce specific, useful outputs.** Always include purpose, context, persona, and constraints.\n- **Vague prompts produce vague results** — the AI can only work with the information it is given.\n- **Iterating on your prompt is the first and most effective troubleshooting step** when an AI response misses the mark.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Create Effective Prompts*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- OpenAI. (2024). *Prompt Engineering Best Practices*. Retrieved from [https://platform.openai.com/docs/guides/prompt-engineering](https://platform.openai.com/docs/guides/prompt-engineering)',
					numFirstSelections: 148,
				},
				{
					id: "e1f2a3b4-1001-4a01-b001-112233440003",
					stem: "Switch to a completely different AI tool, since this one clearly cannot handle the task",
					text: "Switch to a completely different AI tool, since this one clearly cannot handle the task",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "e1f2a3b4-1001-4a01-b001-112233440004",
					stem: "Ask the AI to make the response longer so it covers more ground",
					text: "Ask the AI to make the response longer so it covers more ground",
					isCorrect: false,
					numFirstSelections: 28,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6483,
				type: "Skill",
				label: "Prompt Engineering",
				breadcrumb: "Prompt Engineering",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Effective Prompts",
				"Context and Specificity",
				"Prompt Engineering",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "e1f2a3b4-1001-4a01-b001-112233440002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40102,
		type: "MCSS",
		content: {
			stem: "An employee asks an AI chatbot: \"What were our company's total sales last quarter?\" The AI confidently responds with a specific dollar figure, but the employee knows the AI does not have access to the company's internal financial systems.\n\nWhat has MOST likely occurred?",
			choices: [
				{
					id: "f2a3b4c5-2002-4b02-c002-223344550001",
					stem: "The AI accessed the company's financial database through a hidden connection",
					text: "The AI accessed the company's financial database through a hidden connection",
					isCorrect: false,
					numFirstSelections: 16,
				},
				{
					id: "f2a3b4c5-2002-4b02-c002-223344550002",
					stem: "The AI produced a hallucination — a confident-sounding response that is fabricated rather than based on real data",
					text: "The AI produced a hallucination — a confident-sounding response that is fabricated rather than based on real data",
					isCorrect: true,
					explanation:
						'The AI has **produced a hallucination** — a phenomenon where a large language model generates output that **sounds plausible and confident but is not grounded in factual data**. Hallucinations occur because LLMs are trained to predict the most likely next word in a sequence, not to verify the truth of their statements. When asked a question they cannot answer from real data, they may **fabricate a realistic-sounding response** rather than admitting uncertainty.\n\nThis is one of the **most important limitations** to understand when working with AI assistants. The AI does not have access to private company databases, and it cannot retrieve real-time internal data unless it has been explicitly connected to such systems. When it provides a specific number in this context, it is **generating text that fits the pattern of the question, not reporting an actual figure**.\n\n**Recognizing hallucinations is a critical workplace skill** because acting on fabricated information — especially financial figures — can lead to serious business consequences.\n\nThe other options are **incorrect**:\n\n- **"The AI accessed the company\'s financial database through a hidden connection"** is not how AI chatbots work. Standard AI assistants do not autonomously connect to private systems. If an AI is integrated with internal databases, that connection is **explicitly configured by the organization\'s IT team**, not hidden.\n\n- **"The AI is correct because it was trained on publicly available financial reports from the company"** is unlikely and still unreliable. Even if some financial data appeared in the AI\'s training data, training data has a **cutoff date** and may be incomplete or outdated. More importantly, the employee stated the AI has no access to internal systems, making this explanation implausible for last quarter\'s figures.\n\n- **"The AI deliberately provided a wrong answer to test the employee\'s critical thinking skills"** attributes intent to the AI that it does not have. LLMs do **not have goals, intentions, or strategies**. They generate responses based on statistical patterns in their training data — they do not "test" users or deliberately deceive.\n\n**Key Takeaways:**\n- **AI hallucinations are confident-sounding outputs that are not based on real data.** Always verify critical facts independently.\n- **LLMs predict likely text, not truthful text.** A response that sounds correct may still be entirely fabricated.\n- **Never rely on AI-generated numbers for business decisions** without cross-checking against authoritative sources.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Avoid Hallucinations and Verify Outputs*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Ji, Z., et al. (2023). *Survey of Hallucination in Natural Language Generation*. ACM Computing Surveys. Retrieved from [https://arxiv.org/abs/2202.03629](https://arxiv.org/abs/2202.03629)',
					numFirstSelections: 144,
				},
				{
					id: "f2a3b4c5-2002-4b02-c002-223344550003",
					stem: "The AI is correct because it was trained on publicly available financial reports from the company",
					text: "The AI is correct because it was trained on publicly available financial reports from the company",
					isCorrect: false,
					numFirstSelections: 32,
				},
				{
					id: "f2a3b4c5-2002-4b02-c002-223344550004",
					stem: "The AI deliberately provided a wrong answer to test the employee's critical thinking skills",
					text: "The AI deliberately provided a wrong answer to test the employee's critical thinking skills",
					isCorrect: false,
					numFirstSelections: 14,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6484,
				type: "Skill",
				label: "AI Hallucinations",
				breadcrumb: "AI Hallucinations",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Hallucinations", "AI Limitations", "Output Verification"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "f2a3b4c5-2002-4b02-c002-223344550002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40103,
		type: "MCSS",
		content: {
			stem: "A human resources coordinator wants to use an AI assistant to draft an internal policy document about remote work. The coordinator starts with a broad request and receives a decent first draft, but it lacks the specific sections the company requires.\n\nWhich approach BEST demonstrates working in a conversational loop to improve the output?",
			choices: [
				{
					id: "a3b4c5d6-3003-4c03-d003-334455660001",
					stem: "Delete the first response and start over with a completely new prompt from scratch",
					text: "Delete the first response and start over with a completely new prompt from scratch",
					isCorrect: false,
					numFirstSelections: 24,
				},
				{
					id: "a3b4c5d6-3003-4c03-d003-334455660002",
					stem: "Copy the AI's response into a word processor and manually rewrite the entire document",
					text: "Copy the AI's response into a word processor and manually rewrite the entire document",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "a3b4c5d6-3003-4c03-d003-334455660003",
					stem: "Send follow-up prompts that reference the initial draft, request specific sections be added, and ask the AI to revise the tone and formatting iteratively",
					text: "Send follow-up prompts that reference the initial draft, request specific sections be added, and ask the AI to revise the tone and formatting iteratively",
					isCorrect: true,
					explanation:
						'**Sending follow-up prompts that build on the initial draft** is the best example of working in a **conversational loop** — one of the most effective strategies for getting high-quality output from an AI assistant. Instead of treating the interaction as a one-off transaction, the coordinator **iterates on the response** by providing additional guidance, requesting specific additions, and refining the tone and structure over multiple turns.\n\nThis approach leverages a key strength of modern AI chatbots: they maintain **conversational context** within a session. Each follow-up prompt can reference what came before, allowing the user to **progressively shape the output** toward their exact requirements. This is sometimes called **prompt chaining** — where each prompt in the sequence builds on the result of the previous one.\n\nFor example, the coordinator might say: "Great start. Now add a section on equipment reimbursement and another on time zone expectations. Also, make the tone more formal." This is far more efficient than starting from scratch or manually rewriting everything.\n\nThe other options are **less effective**:\n\n- **"Delete the first response and start over with a completely new prompt from scratch"** discards useful progress. The first draft may have a solid structure and good content — it just needs refinement. Starting over wastes the context already established and forces the user to re-explain everything from the beginning.\n\n- **"Copy the AI\'s response into a word processor and manually rewrite the entire document"** defeats the purpose of using an AI assistant. While final human editing is always recommended, **manually rewriting the entire document** is inefficient when the AI can handle the revisions through follow-up prompts.\n\n- **"Accept the first draft as-is since the AI\'s initial output is always the best version"** is incorrect. AI outputs are rarely perfect on the first attempt, especially for complex documents with specific organizational requirements. **Iteration is expected and encouraged** — the first response is a starting point, not a final product.\n\n**Key Takeaways:**\n- **Treat AI interactions as conversations, not one-shot transactions.** Iterative follow-up prompts consistently produce better results.\n- **Prompt chaining** allows you to build on previous responses, adding specificity and refinement with each turn.\n- **The first AI response is a starting point** — always plan to iterate, refine, and guide the output toward your specific needs.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Work in Conversational Loops*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Anthropic. (2024). *Prompt Engineering — Iterative Prompting*. Retrieved from [https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering](https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering)',
					numFirstSelections: 146,
				},
				{
					id: "a3b4c5d6-3003-4c03-d003-334455660004",
					stem: "Accept the first draft as-is since the AI's initial output is always the best version",
					text: "Accept the first draft as-is since the AI's initial output is always the best version",
					isCorrect: false,
					numFirstSelections: 12,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6483,
				type: "Skill",
				label: "Prompt Engineering",
				breadcrumb: "Prompt Engineering",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Conversational Loops", "Prompt Chaining", "Iteration"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "a3b4c5d6-3003-4c03-d003-334455660003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40104,
		type: "MCSS",
		content: {
			stem: "A customer service representative is considering using a public AI chatbot to help draft responses to customer complaints. Some of the complaints include customers' full names, account numbers, and order details.\n\nWhat should the representative do BEFORE pasting customer information into the AI tool?",
			choices: [
				{
					id: "b4c5d6e7-4004-4d04-e004-445566770001",
					stem: "Proceed immediately, since AI tools are always secure and encrypted",
					text: "Proceed immediately, since AI tools are always secure and encrypted",
					isCorrect: false,
					numFirstSelections: 14,
				},
				{
					id: "b4c5d6e7-4004-4d04-e004-445566770002",
					stem: "Remove or anonymize all personally identifiable information and check the organization's AI usage policy before inputting any data",
					text: "Remove or anonymize all personally identifiable information and check the organization's AI usage policy before inputting any data",
					isCorrect: true,
					explanation:
						'**Removing or anonymizing personally identifiable information (PII) and consulting the organization\'s AI usage policy** is the correct first step before inputting any sensitive data into an AI tool. Public AI chatbots typically **process and may store user inputs** for model improvement, which means any customer data entered could potentially be exposed, logged, or used in ways that violate **data privacy regulations** (such as GDPR, CCPA, or HIPAA) and company policies.\n\n**Personally identifiable information** — including names, account numbers, email addresses, and order details — should never be entered into a public AI tool without first understanding how that data will be handled. Most organizations have **AI usage policies** that specify what types of data can and cannot be shared with external AI services. Checking this policy is not optional — it is a **professional responsibility**.\n\nThe recommended approach is to **anonymize the data** (e.g., replace "John Smith, Account #12345" with "Customer A, Account #XXXXX") before pasting it into the AI tool. This allows the representative to still benefit from the AI\'s drafting assistance without exposing sensitive information.\n\nThe other options are **risky or incorrect**:\n\n- **"Proceed immediately, since AI tools are always secure and encrypted"** is a dangerous assumption. While many AI services use encryption in transit, this does **not mean the data is private**. Many public AI tools retain user inputs for training or quality improvement. "Encrypted" does not mean "confidential" in this context.\n\n- **"Only use the AI tool during non-business hours to reduce the risk of data interception"** is based on a misunderstanding of how data security works. The time of day has **no effect on data privacy or security**. Data entered into a public AI tool carries the same risks regardless of when it is submitted.\n\n- **"Ask the AI chatbot whether it is safe to share customer data with it"** is unreliable. An AI chatbot **cannot accurately assess its own data handling practices** or make binding assurances about privacy. The AI may generate a reassuring response that has no contractual or legal validity. Data handling policies must be verified through the **tool\'s official documentation and your organization\'s IT/legal team**.\n\n**Key Takeaways:**\n- **Never enter PII or sensitive data into a public AI tool** without first checking your organization\'s AI usage policy.\n- **Anonymize data before sharing it with AI tools** — replace real names, account numbers, and other identifiers with placeholders.\n- **"Encrypted" does not mean "private."** Understand how the AI tool stores, processes, and retains your inputs.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Use AI Responsibly with Sensitive Information*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- NIST. (2024). *Artificial Intelligence Risk Management Framework (AI RMF 1.0)*. Retrieved from [https://www.nist.gov/artificial-intelligence/executive-order-safe-secure-and-trustworthy-artificial-intelligence](https://www.nist.gov/artificial-intelligence/executive-order-safe-secure-and-trustworthy-artificial-intelligence)',
					numFirstSelections: 152,
				},
				{
					id: "b4c5d6e7-4004-4d04-e004-445566770003",
					stem: "Only use the AI tool during non-business hours to reduce the risk of data interception",
					text: "Only use the AI tool during non-business hours to reduce the risk of data interception",
					isCorrect: false,
					numFirstSelections: 12,
				},
				{
					id: "b4c5d6e7-4004-4d04-e004-445566770004",
					stem: "Ask the AI chatbot whether it is safe to share customer data with it",
					text: "Ask the AI chatbot whether it is safe to share customer data with it",
					isCorrect: false,
					numFirstSelections: 34,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6486,
				type: "Skill",
				label: "Practical AI Use Cases",
				breadcrumb: "Practical AI Use Cases",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Data Privacy", "Sensitive Information", "Responsible AI Use"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "b4c5d6e7-4004-4d04-e004-445566770002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40105,
		type: "MCSS",
		content: {
			stem: "A team lead asks an AI assistant to summarize a lengthy research article. The AI produces a clear, well-structured summary. The team lead plans to share this summary with executives to inform a strategic decision.\n\nWhat is the MOST important step the team lead should take before sharing the summary?",
			choices: [
				{
					id: "c5d6e7f8-5005-4e05-f005-556677880001",
					stem: "Check that the summary formatting matches the company's style guide",
					text: "Check that the summary formatting matches the company's style guide",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "c5d6e7f8-5005-4e05-f005-556677880002",
					stem: "Verify the AI-generated summary against the original article to ensure key claims are accurate and nothing critical has been fabricated or omitted",
					text: "Verify the AI-generated summary against the original article to ensure key claims are accurate and nothing critical has been fabricated or omitted",
					isCorrect: true,
					explanation:
						'**Verifying the AI-generated summary against the original source material** is the most important step before sharing it with decision-makers. AI assistants can produce summaries that are well-written and appear authoritative, but they may **introduce inaccuracies, omit critical nuances, or subtly misrepresent the original findings**. This is especially dangerous in business contexts where executives may make strategic decisions based on the summary.\n\nVerification involves **comparing the AI\'s key claims against the original article** to ensure factual accuracy, checking that important caveats or limitations are not omitted, and confirming that the summary does not overstate or understate the source material\'s conclusions. This is a core **output verification strategy** — treating the AI\'s output as a draft that requires human review, not a finished product.\n\nThe other options are **secondary or insufficient**:\n\n- **"Check that the summary formatting matches the company\'s style guide"** is a valid cosmetic step, but it does **not address the accuracy of the content**. A perfectly formatted summary that contains fabricated claims is far more dangerous than a rough draft that is factually correct.\n\n- **"Ask the AI to confirm that its summary is accurate"** is unreliable. AI assistants **cannot independently verify the truth of their own outputs**. When asked "Is this accurate?", the AI will typically respond affirmatively based on the pattern of the conversation — not because it has cross-checked its work. This creates a **false sense of validation**.\n\n- **"Share the summary immediately to save time, since the AI is generally reliable"** prioritizes speed over accuracy. While AI assistants are often helpful, they are **not infallible** — especially when summarizing complex or nuanced material. In a high-stakes context like executive decision-making, the time saved by skipping verification is not worth the risk of sharing inaccurate information.\n\n**Key Takeaways:**\n- **Always verify AI-generated outputs against original sources** before using them for important decisions.\n- **AI summaries can sound authoritative while containing errors** — plausibility is not the same as accuracy.\n- **Treat AI outputs as drafts, not final products.** Human review is a non-negotiable step in any professional workflow.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Avoid Hallucinations and Verify Outputs*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Eloundou, T., et al. (2023). *GPTs are GPTs: An Early Look at the Labor Market Impact Potential of Large Language Models*. arXiv. Retrieved from [https://arxiv.org/abs/2303.10130](https://arxiv.org/abs/2303.10130)',
					numFirstSelections: 142,
				},
				{
					id: "c5d6e7f8-5005-4e05-f005-556677880003",
					stem: "Ask the AI to confirm that its summary is accurate",
					text: "Ask the AI to confirm that its summary is accurate",
					isCorrect: false,
					numFirstSelections: 30,
				},
				{
					id: "c5d6e7f8-5005-4e05-f005-556677880004",
					stem: "Share the summary immediately to save time, since the AI is generally reliable",
					text: "Share the summary immediately to save time, since the AI is generally reliable",
					isCorrect: false,
					numFirstSelections: 20,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6485,
				type: "Skill",
				label: "Output Verification",
				breadcrumb: "Output Verification",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Output Verification",
				"Hallucination Prevention",
				"Critical Thinking",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "c5d6e7f8-5005-4e05-f005-556677880002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40106,
		type: "MCSS",
		content: {
			stem: 'A project manager is struggling with a "blank page" problem — they need to create a project risk assessment document but don\'t know where to start.\n\nWhich of the following is the MOST effective way to use an AI assistant in this situation?',
			choices: [
				{
					id: "d6e7f8a9-6006-4f06-a006-667788990001",
					stem: "Ask the AI to autonomously identify all real risks for the specific project without providing any project details",
					text: "Ask the AI to autonomously identify all real risks for the specific project without providing any project details",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "d6e7f8a9-6006-4f06-a006-667788990002",
					stem: "Ask the AI to generate an initial outline and common risk categories for the project type, then refine and customize the output with project-specific details",
					text: "Ask the AI to generate an initial outline and common risk categories for the project type, then refine and customize the output with project-specific details",
					isCorrect: true,
					explanation:
						'**Using the AI to generate a starting framework and then customizing it** is the most effective way to overcome a "blank page" problem. AI assistants excel at producing **structured outlines, common templates, and category lists** that give users a foundation to build upon. By asking for an initial outline of risk categories relevant to the project type (e.g., software development, construction, product launch), the project manager gets a **structured starting point** that breaks the creative block.\n\nThe key is that the AI provides the **scaffold**, and the human provides the **project-specific knowledge**. This is one of the highest-value practical use cases for AI in the workplace — it does not replace the project manager\'s expertise, but it **accelerates the initial phase** where getting started is the hardest part.\n\nAfter receiving the outline, the project manager can iterate using follow-up prompts: "Add a section on vendor dependency risks," "Reformat this as a table with likelihood and impact columns," etc. This leverages the **conversational loop** approach for progressive refinement.\n\nThe other options are **less effective**:\n\n- **"Ask the AI to autonomously identify all real risks for the specific project without providing any project details"** will produce **generic, potentially irrelevant output**. Without information about the project\'s scope, timeline, technology, team, or industry, the AI cannot identify specific risks that actually apply. This also risks creating a **false sense of completeness** — the manager might assume all real risks are covered when they are not.\n\n- **"Avoid using AI for this task because risk assessment requires human judgment"** is unnecessarily restrictive. While **final risk evaluation absolutely requires human judgment**, AI is valuable for the **initial brainstorming and structuring phase**. Using AI as a starting point does not mean abdicating professional responsibility — it means working more efficiently.\n\n- **"Ask the AI to find and copy a risk assessment template from the internet"** raises **copyright and relevance concerns**. A template copied from an unknown source may not fit the organization\'s standards, may be outdated, and may not be licensed for reuse. Having the AI **generate an original outline** tailored to the user\'s description is both more flexible and more appropriate.\n\n**Key Takeaways:**\n- **AI assistants are excellent at overcoming "blank page" moments** by generating initial outlines, frameworks, and category lists.\n- **The AI provides structure; the human provides expertise.** Always customize and refine AI-generated starting points with your domain knowledge.\n- **Combining AI scaffolding with iterative refinement** is one of the most practical and high-value workplace AI use cases.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Identify Practical AI Use Cases*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Harvard Business Review. (2023). *How People Are Really Using GenAI*. Retrieved from [https://hbr.org/2023/09/how-people-are-really-using-genai](https://hbr.org/2023/09/how-people-are-really-using-genai)',
					numFirstSelections: 148,
				},
				{
					id: "d6e7f8a9-6006-4f06-a006-667788990003",
					stem: "Avoid using AI for this task because risk assessment requires human judgment",
					text: "Avoid using AI for this task because risk assessment requires human judgment",
					isCorrect: false,
					numFirstSelections: 26,
				},
				{
					id: "d6e7f8a9-6006-4f06-a006-667788990004",
					stem: "Ask the AI to find and copy a risk assessment template from the internet",
					text: "Ask the AI to find and copy a risk assessment template from the internet",
					isCorrect: false,
					numFirstSelections: 16,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6486,
				type: "Skill",
				label: "Practical AI Use Cases",
				breadcrumb: "Practical AI Use Cases",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Practical AI Use Cases",
				"Blank Page Problem",
				"Workflow Integration",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "d6e7f8a9-6006-4f06-a006-667788990002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40107,
		type: "MCSS",
		content: {
			stem: "Which of the following BEST describes how a large language model (LLM) generates its responses?",
			choices: [
				{
					id: "e7f8a9b0-7007-4a07-b007-778899aa0001",
					stem: "It searches a live database of facts and retrieves the correct answer to each question",
					text: "It searches a live database of facts and retrieves the correct answer to each question",
					isCorrect: false,
					numFirstSelections: 32,
				},
				{
					id: "e7f8a9b0-7007-4a07-b007-778899aa0002",
					stem: "It predicts the most likely next word in a sequence based on patterns learned from large amounts of training data",
					text: "It predicts the most likely next word in a sequence based on patterns learned from large amounts of training data",
					isCorrect: true,
					explanation:
						'**LLMs generate text by predicting the most probable next token (word or word-piece) in a sequence**, based on patterns learned during training on vast amounts of text data. This is the fundamental mechanism behind all modern large language models. The model does not "know" facts in the way a database does — instead, it has learned **statistical associations between words, phrases, and concepts** from its training corpus.\n\nUnderstanding this mechanism is essential because it explains both the **strengths and limitations** of AI assistants. The strength is that LLMs can produce remarkably fluent, coherent, and contextually appropriate text across a wide range of topics. The limitation is that they can also generate **plausible-sounding but incorrect information** (hallucinations) because their goal is to produce likely text, not verified truth.\n\nThis is why AI assistants sometimes "make things up" — they are completing a text pattern, not looking up answers in a reliable source. Recognizing this distinction helps users set appropriate expectations and apply the right verification strategies.\n\nThe other options reflect **common misconceptions**:\n\n- **"It searches a live database of facts and retrieves the correct answer to each question"** describes a **search engine or database query system**, not an LLM. LLMs do not have real-time access to a fact database (unless specifically augmented with retrieval tools). Their responses come from patterns encoded in their model weights during training.\n\n- **"It uses a set of pre-written response templates and selects the one that best matches the user\'s input"** describes a **rule-based chatbot or template matching system** — a much older and simpler technology. LLMs generate novel text on every response; they do not select from a fixed library of pre-written answers.\n\n- **"It understands the meaning of every question and reasons through the answer logically like a human expert"** overstates the model\'s capabilities. While LLMs can **simulate reasoning** impressively, they do not possess true understanding or logical reasoning in the way humans do. Their outputs are the result of **pattern matching at scale**, which can mimic reasoning but is not equivalent to it.\n\n**Key Takeaways:**\n- **LLMs generate text through next-token prediction**, not fact retrieval, template matching, or true reasoning.\n- **This mechanism explains both AI\'s fluency and its tendency to hallucinate** — it produces likely text, not guaranteed truth.\n- **Understanding how AI works helps you use it more effectively** and set realistic expectations for its outputs.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Understand Generative AI and Chatbots*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Vaswani, A., et al. (2017). *Attention Is All You Need*. NeurIPS 2017. Retrieved from [https://arxiv.org/abs/1706.03762](https://arxiv.org/abs/1706.03762)',
					numFirstSelections: 138,
				},
				{
					id: "e7f8a9b0-7007-4a07-b007-778899aa0003",
					stem: "It uses a set of pre-written response templates and selects the one that best matches the user's input",
					text: "It uses a set of pre-written response templates and selects the one that best matches the user's input",
					isCorrect: false,
					numFirstSelections: 24,
				},
				{
					id: "e7f8a9b0-7007-4a07-b007-778899aa0004",
					stem: "It understands the meaning of every question and reasons through the answer logically like a human expert",
					text: "It understands the meaning of every question and reasons through the answer logically like a human expert",
					isCorrect: false,
					numFirstSelections: 18,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6483,
				type: "Skill",
				label: "Prompt Engineering",
				breadcrumb: "Prompt Engineering",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Large Language Models",
				"Generative AI Fundamentals",
				"Next-Token Prediction",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "e7f8a9b0-7007-4a07-b007-778899aa0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40108,
		type: "MCSS",
		content: {
			stem: 'A sales analyst wants an AI assistant to help analyze a set of quarterly performance numbers. The analyst writes the following prompt:\n\n"You are a senior sales analyst. Given the following quarterly revenue figures for our three product lines, identify the product line with the strongest growth trend and explain why. Focus only on the data I provide — do not speculate beyond the numbers."\n\nWhich prompt engineering elements has the analyst effectively used in this prompt?',
			choices: [
				{
					id: "f8a9b0c1-8008-4b08-c008-8899aabb0001",
					stem: "Only a persona — the rest of the prompt is unstructured",
					text: "Only a persona — the rest of the prompt is unstructured",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "f8a9b0c1-8008-4b08-c008-8899aabb0002",
					stem: "A persona, a clear purpose, context reference, and a constraint limiting speculation",
					text: "A persona, a clear purpose, context reference, and a constraint limiting speculation",
					isCorrect: true,
					explanation:
						'The analyst\'s prompt effectively uses **four key prompt engineering elements**:\n\n1. **Persona**: "You are a senior sales analyst" — this tells the AI what role to adopt, shaping the vocabulary, depth, and perspective of the response.\n2. **Purpose**: "identify the product line with the strongest growth trend and explain why" — this clearly states **what the AI should do** and what the expected output looks like.\n3. **Context reference**: "Given the following quarterly revenue figures for our three product lines" — this signals that specific data is being provided and the AI should base its analysis on that data.\n4. **Constraint**: "Focus only on the data I provide — do not speculate beyond the numbers" — this sets a **guardrail** that limits the AI to the provided information and reduces the risk of hallucination or unsupported extrapolation.\n\nUsing all four elements together produces a **well-structured, focused prompt** that maximizes the chance of getting a relevant, accurate, and useful response. This is a textbook example of effective prompt construction for workplace tasks.\n\nThe other options are **incomplete assessments**:\n\n- **"Only a persona — the rest of the prompt is unstructured"** ignores the clear purpose statement, context reference, and explicit constraint. The prompt is well-structured with multiple intentional elements working together.\n\n- **"A persona and a purpose, but no constraints or context"** overlooks the constraint ("do not speculate beyond the numbers") and the context reference ("the following quarterly revenue figures"). Both are explicitly present in the prompt.\n\n- **"No effective elements — the prompt is too long and should be shortened to a single sentence"** is incorrect. **Effective prompts are not defined by brevity** — they are defined by clarity and completeness. A prompt that includes persona, purpose, context, and constraints is **well-crafted, not overlong**. Condensing it into a single vague sentence would degrade the quality of the AI\'s response.\n\n**Key Takeaways:**\n- **Effective prompts combine persona, purpose, context, and constraints** to guide the AI toward the desired output.\n- **Constraints are especially important** for data-focused tasks — they prevent the AI from speculating beyond the provided information.\n- **Longer, well-structured prompts outperform short, vague ones** — clarity and specificity are more important than brevity.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Create Effective Prompts*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Google. (2024). *Introduction to Prompt Design*. Retrieved from [https://cloud.google.com/vertex-ai/generative-ai/docs/learn/prompts/introduction-prompt-design](https://cloud.google.com/vertex-ai/generative-ai/docs/learn/prompts/introduction-prompt-design)',
					numFirstSelections: 144,
				},
				{
					id: "f8a9b0c1-8008-4b08-c008-8899aabb0003",
					stem: "A persona and a purpose, but no constraints or context",
					text: "A persona and a purpose, but no constraints or context",
					isCorrect: false,
					numFirstSelections: 28,
				},
				{
					id: "f8a9b0c1-8008-4b08-c008-8899aabb0004",
					stem: "No effective elements — the prompt is too long and should be shortened to a single sentence",
					text: "No effective elements — the prompt is too long and should be shortened to a single sentence",
					isCorrect: false,
					numFirstSelections: 12,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6483,
				type: "Skill",
				label: "Prompt Engineering",
				breadcrumb: "Prompt Engineering",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Prompt Engineering", "Persona", "Constraints"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "f8a9b0c1-8008-4b08-c008-8899aabb0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40109,
		type: "MCSS",
		content: {
			stem: "A legal assistant uses an AI chatbot to research whether a specific regulation applies to their company's new product line. The AI provides a detailed response citing a specific section of a government regulation.\n\nWhich verification strategy should the legal assistant use to validate the AI's response?",
			choices: [
				{
					id: "a9b0c1d2-9009-4c09-d009-99aabbcc0001",
					stem: "Accept the response because the AI cited a specific regulation section, which indicates it is accurate",
					text: "Accept the response because the AI cited a specific regulation section, which indicates it is accurate",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "a9b0c1d2-9009-4c09-d009-99aabbcc0002",
					stem: "Ask the AI a second time if the citation is correct and trust its confirmation",
					text: "Ask the AI a second time if the citation is correct and trust its confirmation",
					isCorrect: false,
					numFirstSelections: 16,
				},
				{
					id: "a9b0c1d2-9009-4c09-d009-99aabbcc0003",
					stem: "Look up the cited regulation section in an official government source to confirm it exists and says what the AI claims",
					text: "Look up the cited regulation section in an official government source to confirm it exists and says what the AI claims",
					isCorrect: true,
					explanation:
						'**Looking up the cited regulation in an official government source** is the correct and most reliable verification strategy. AI assistants are known to **fabricate citations** — they may generate regulation section numbers, case names, or reference details that **sound legitimate but do not actually exist**. This is a well-documented form of hallucination that is particularly dangerous in legal, regulatory, and compliance contexts.\n\nThe verification process should involve two checks: (1) **confirm that the cited regulation section actually exists** (the AI may have invented a plausible-sounding section number), and (2) **confirm that the content of the regulation matches what the AI claimed** (the AI may cite a real section but misrepresent what it says). Official government databases, legislative websites, and authoritative legal resources are the appropriate sources for this verification.\n\nThis is a **non-negotiable step in any professional workflow** where accuracy has legal or financial consequences.\n\nThe other options are **inadequate**:\n\n- **"Accept the response because the AI cited a specific regulation section, which indicates it is accurate"** is a critical mistake. The **presence of a citation does not guarantee its accuracy**. AI models generate citations using the same predictive mechanism they use for all other text — they produce what looks like a plausible citation, regardless of whether it is real. Many documented cases exist of AI models citing completely fictitious legal cases and regulation sections.\n\n- **"Ask the AI a second time if the citation is correct and trust its confirmation"** is unreliable. The AI **cannot independently verify its own outputs**. When asked to confirm a previous response, it will typically affirm it — not because it has checked, but because affirming the previous answer is the most statistically likely conversational response. This creates a **false feedback loop** that reinforces unverified information.\n\n- **"Ask a colleague to read the AI\'s response and judge whether it sounds reasonable"** is insufficient for legal and regulatory information. A colleague reading the response can only assess whether it **sounds plausible**, not whether it is **factually correct**. Plausibility is exactly what makes hallucinations dangerous — they are designed by the model\'s training to sound convincing. Only verification against the **primary authoritative source** can confirm accuracy.\n\n**Key Takeaways:**\n- **AI-generated citations are frequently fabricated.** Always verify them against primary sources.\n- **Official government and legal databases are the only reliable verification source** for regulatory information.\n- **Asking the AI to confirm its own work is not verification** — it is a feedback loop that can reinforce errors.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Avoid Hallucinations and Verify Outputs*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- Dahl, M., et al. (2024). *Large Legal Fictions: Profiling Legal Hallucinations in Large Language Models*. Journal of Legal Analysis. Retrieved from [https://arxiv.org/abs/2401.01301](https://arxiv.org/abs/2401.01301)',
					numFirstSelections: 150,
				},
				{
					id: "a9b0c1d2-9009-4c09-d009-99aabbcc0004",
					stem: "Ask a colleague to read the AI's response and judge whether it sounds reasonable",
					text: "Ask a colleague to read the AI's response and judge whether it sounds reasonable",
					isCorrect: false,
					numFirstSelections: 26,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6485,
				type: "Skill",
				label: "Output Verification",
				breadcrumb: "Output Verification",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Verification Strategies",
				"Fabricated Citations",
				"Critical Evaluation",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "a9b0c1d2-9009-4c09-d009-99aabbcc0003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 40110,
		type: "MCSS",
		content: {
			stem: "A department manager wants to identify tasks within her team's workflow that would benefit MOST from AI assistant support. The team handles a mix of routine email responses, creative brand strategy development, confidential employee performance reviews, and meeting agenda preparation.\n\nWhich task is the BEST candidate for AI assistant support?",
			choices: [
				{
					id: "b0c1d2e3-1010-4d10-e010-aabbccdd0001",
					stem: "Creative brand strategy development, because AI excels at generating original strategic vision",
					text: "Creative brand strategy development, because AI excels at generating original strategic vision",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "b0c1d2e3-1010-4d10-e010-aabbccdd0002",
					stem: "Confidential employee performance reviews, because AI can ensure objectivity in evaluations",
					text: "Confidential employee performance reviews, because AI can ensure objectivity in evaluations",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "b0c1d2e3-1010-4d10-e010-aabbccdd0003",
					stem: "Routine email responses and meeting agenda preparation, because these are repetitive, structured tasks where AI can save significant time",
					text: "Routine email responses and meeting agenda preparation, because these are repetitive, structured tasks where AI can save significant time",
					isCorrect: true,
					explanation:
						'**Routine email responses and meeting agenda preparation** are the best candidates for AI assistant support because they are **repetitive, structured, and relatively low-risk tasks** where AI can deliver significant time savings. These tasks follow predictable patterns, have clear expected formats, and do not require deep original thinking or access to highly confidential data.\n\nAI assistants are most valuable when applied to tasks that are **time-consuming but not complex** — the kind of work that consumes a disproportionate amount of an employee\'s day without requiring their unique expertise. Drafting a standard meeting agenda or composing a routine acknowledgment email are perfect examples: the AI can produce a solid first draft in seconds, which the employee can quickly review and personalize.\n\nThis is a core principle of **identifying practical AI use cases**: look for tasks that are high-volume, repetitive, and structured, where the cost of an occasional imperfection is low and human review can catch any issues.\n\nThe other options are **less suitable**:\n\n- **"Creative brand strategy development, because AI excels at generating original strategic vision"** overstates AI\'s capabilities. AI can **assist with brainstorming and generating initial ideas**, but it does not possess original creative vision or deep understanding of a brand\'s unique market position. Strategic brand development requires **human judgment, market intuition, and organizational knowledge** that AI cannot replicate. AI is a useful brainstorming tool here, but it is not the best primary candidate.\n\n- **"Confidential employee performance reviews, because AI can ensure objectivity in evaluations"** raises serious **data privacy and sensitivity concerns**. Performance reviews contain **personal, confidential information** that should not be entered into public AI tools. Additionally, AI cannot guarantee objectivity — it may reproduce biases present in its training data. Performance evaluations require **nuanced human judgment** about individual circumstances.\n\n- **"All four tasks equally, since AI assistants are equally effective at every type of work"** is incorrect. AI assistants have **varying levels of effectiveness depending on the task type**. They excel at structured, repetitive tasks and struggle with tasks requiring deep judgment, access to confidential data, or truly original creative thinking. Effective AI adoption requires **matching the tool to the right tasks**.\n\n**Key Takeaways:**\n- **Repetitive, structured, low-risk tasks** are the best candidates for AI assistant support in the workplace.\n- **AI saves the most time** on high-volume tasks that follow predictable patterns — emails, agendas, summaries, first drafts.\n- **Not all tasks benefit equally from AI.** Tasks involving confidential data, high-stakes decisions, or original creative strategy require more human involvement.\n\n**References:**\n- CompTIA. (2024). *AI Essentials Certification — Identify Practical AI Use Cases in Your Role*. Retrieved from [https://www.comptia.org/certifications/ai-essentials](https://www.comptia.org/certifications/ai-essentials)\n- McKinsey & Company. (2023). *The Economic Potential of Generative AI*. Retrieved from [https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier](https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier)',
					numFirstSelections: 146,
				},
				{
					id: "b0c1d2e3-1010-4d10-e010-aabbccdd0004",
					stem: "All four tasks equally, since AI assistants are equally effective at every type of work",
					text: "All four tasks equally, since AI assistants are equally effective at every type of work",
					isCorrect: false,
					numFirstSelections: 14,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 6486,
				type: "Skill",
				label: "Practical AI Use Cases",
				breadcrumb: "Practical AI Use Cases",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Practical AI Use Cases",
				"Task Suitability",
				"Workflow Integration",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "b0c1d2e3-1010-4d10-e010-aabbccdd0003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
];
